import { WorkflowTemplate } from '@/types/workflow';

export const workflowTemplates: WorkflowTemplate[] = [
  {
    id: 'lead-nurture-sequence',
    name: 'Lead Nurture Sequence',
    description: 'Automated email sequence for nurturing new leads with AI personalization',
    category: 'lead-generation',
    icon: 'Mail',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          position: { x: 100, y: 200 },
          data: {
            label: 'New Lead Added',
            type: 'trigger',
            config: { triggerType: 'lead_created' },
            icon: 'User',
            color: '#22c55e',
            outputs: [{ id: 'trigger', label: 'Lead Data', type: 'trigger' }],
          },
        },
        {
          id: 'ai_personalize_1',
          type: 'ai',
          position: { x: 400, y: 200 },
          data: {
            label: 'Personalize Welcome Email',
            type: 'ai',
            config: {
              prompt: `Generate a personalized welcome email for this lead:
              Lead Name: {{input.name}}
              Company: {{input.company}}
              Industry: {{input.industry}}
              
              Make it warm, professional, and mention something specific about their industry.`,
              model: 'gpt-4',
            },
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Lead Data', type: 'data', required: true }],
            outputs: [{ id: 'generated', label: 'Email Content', type: 'data' }],
          },
        },
        {
          id: 'send_email_1',
          type: 'action',
          position: { x: 700, y: 200 },
          data: {
            label: 'Send Welcome Email',
            type: 'action',
            config: {
              actionType: 'send_email',
              email: {
                to: '{{trigger.email}}',
                subject: 'Welcome to {{trigger.company}}!',
                body: '{{ai_personalize_1_output.generated}}',
              },
            },
            icon: 'Send',
            color: '#3b82f6',
            inputs: [{ id: 'input', label: 'Email Content', type: 'data', required: true }],
            outputs: [{ id: 'success', label: 'Sent', type: 'data' }],
          },
        },
        {
          id: 'delay_1',
          type: 'delay',
          position: { x: 1000, y: 200 },
          data: {
            label: 'Wait 3 Days',
            type: 'delay',
            config: { duration: 3, unit: 'days' },
            icon: 'Clock',
            color: '#f59e0b',
            inputs: [{ id: 'input', label: 'Input', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Output', type: 'data' }],
          },
        },
        {
          id: 'send_email_2',
          type: 'action',
          position: { x: 1300, y: 200 },
          data: {
            label: 'Send Follow-up Email',
            type: 'action',
            config: {
              actionType: 'send_email',
              email: {
                to: '{{trigger.email}}',
                subject: 'Quick question about {{trigger.company}}',
                body: 'Follow-up email content...',
              },
            },
            icon: 'Send',
            color: '#3b82f6',
            inputs: [{ id: 'input', label: 'Input', type: 'data', required: true }],
            outputs: [{ id: 'success', label: 'Sent', type: 'data' }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'trigger_1', target: 'ai_personalize_1', sourceHandle: 'trigger', targetHandle: 'input' },
        { id: 'e2', source: 'ai_personalize_1', target: 'send_email_1', sourceHandle: 'generated', targetHandle: 'input' },
        { id: 'e3', source: 'send_email_1', target: 'delay_1', sourceHandle: 'success', targetHandle: 'input' },
        { id: 'e4', source: 'delay_1', target: 'send_email_2', sourceHandle: 'output', targetHandle: 'input' },
      ],
    },
  },
  {
    id: 'lead-scoring-automation',
    name: 'AI Lead Scoring',
    description: 'Automatically score and categorize leads based on their profile and behavior',
    category: 'lead-generation',
    icon: 'BarChart',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          position: { x: 100, y: 200 },
          data: {
            label: 'Lead Updated',
            type: 'trigger',
            config: { triggerType: 'lead_updated' },
            icon: 'User',
            color: '#22c55e',
            outputs: [{ id: 'trigger', label: 'Lead Data', type: 'trigger' }],
          },
        },
        {
          id: 'ai_score_1',
          type: 'ai',
          position: { x: 400, y: 200 },
          data: {
            label: 'Calculate Lead Score',
            type: 'ai',
            config: {
              prompt: `Analyze this lead and provide a score from 0-100:
              Lead: {{input}}
              
              Consider: company size, industry, engagement level, job title, and any other relevant factors.
              Respond with JSON: {"score": 0-100, "reasoning": "...", "recommendations": ["..."]}`,
              model: 'gpt-4',
            },
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Lead Data', type: 'data', required: true }],
            outputs: [{ id: 'generated', label: 'Score Analysis', type: 'data' }],
          },
        },
        {
          id: 'condition_1',
          type: 'condition',
          position: { x: 700, y: 200 },
          data: {
            label: 'High Score Check',
            type: 'condition',
            config: {
              conditions: [{ field: 'score', operator: 'greater_than', value: 70 }],
              logic: 'and',
            },
            icon: 'GitBranch',
            color: '#f59e0b',
            inputs: [{ id: 'input', label: 'Score Data', type: 'data', required: true }],
            outputs: [
              { id: 'true', label: 'High Score', type: 'condition' },
              { id: 'false', label: 'Low Score', type: 'condition' },
            ],
          },
        },
        {
          id: 'update_lead_1',
          type: 'action',
          position: { x: 1000, y: 100 },
          data: {
            label: 'Mark as Hot Lead',
            type: 'action',
            config: {
              actionType: 'update_lead',
              lead: { field: 'status', value: 'hot', operation: 'set' },
            },
            icon: 'Database',
            color: '#3b82f6',
            inputs: [{ id: 'input', label: 'Lead Data', type: 'data', required: true }],
            outputs: [{ id: 'updated', label: 'Updated', type: 'data' }],
          },
        },
        {
          id: 'add_sequence_1',
          type: 'action',
          position: { x: 1000, y: 300 },
          data: {
            label: 'Add to Nurture Sequence',
            type: 'action',
            config: {
              actionType: 'add_to_sequence',
              sequence_id: 'nurture_sequence',
            },
            icon: 'MessageSquare',
            color: '#3b82f6',
            inputs: [{ id: 'lead', label: 'Lead', type: 'data', required: true }],
            outputs: [{ id: 'added', label: 'Added', type: 'data' }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'trigger_1', target: 'ai_score_1', sourceHandle: 'trigger', targetHandle: 'input' },
        { id: 'e2', source: 'ai_score_1', target: 'condition_1', sourceHandle: 'generated', targetHandle: 'input' },
        { id: 'e3', source: 'condition_1', target: 'update_lead_1', sourceHandle: 'true', targetHandle: 'input' },
        { id: 'e4', source: 'condition_1', target: 'add_sequence_1', sourceHandle: 'false', targetHandle: 'lead' },
      ],
    },
  },
  {
    id: 'customer-support-ai',
    name: 'AI Customer Support',
    description: 'Automatically respond to customer inquiries with AI-generated responses',
    category: 'customer-support',
    icon: 'MessageSquare',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          position: { x: 100, y: 200 },
          data: {
            label: 'Email Received',
            type: 'trigger',
            config: { triggerType: 'email_received' },
            icon: 'Mail',
            color: '#22c55e',
            outputs: [{ id: 'trigger', label: 'Email Data', type: 'trigger' }],
          },
        },
        {
          id: 'ai_classify_1',
          type: 'ai',
          position: { x: 400, y: 200 },
          data: {
            label: 'Classify Intent',
            type: 'ai',
            config: {
              prompt: `Classify this customer email into categories:
              Email: {{input.body}}
              
              Categories: inquiry, complaint, feature_request, billing, technical_support, other
              Respond with JSON: {"category": "...", "confidence": 0.0-1.0, "sentiment": "positive/neutral/negative"}`,
              model: 'gpt-3.5-turbo',
            },
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Email', type: 'data', required: true }],
            outputs: [{ id: 'generated', label: 'Classification', type: 'data' }],
          },
        },
        {
          id: 'condition_1',
          type: 'condition',
          position: { x: 700, y: 200 },
          data: {
            label: 'Check Urgency',
            type: 'condition',
            config: {
              conditions: [
                { field: 'category', operator: 'equals', value: 'complaint' },
                { field: 'sentiment', operator: 'equals', value: 'negative' },
              ],
              logic: 'or',
            },
            icon: 'GitBranch',
            color: '#f59e0b',
            inputs: [{ id: 'input', label: 'Classification', type: 'data', required: true }],
            outputs: [
              { id: 'true', label: 'Urgent', type: 'condition' },
              { id: 'false', label: 'Normal', type: 'condition' },
            ],
          },
        },
        {
          id: 'ai_respond_1',
          type: 'ai',
          position: { x: 1000, y: 300 },
          data: {
            label: 'Generate Response',
            type: 'ai',
            config: {
              prompt: `Generate a helpful customer support response:
              Customer Email: {{trigger.body}}
              Category: {{ai_classify_1_output.category}}
              
              Be professional, empathetic, and provide actionable next steps.`,
              model: 'gpt-4',
            },
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Context', type: 'data', required: true }],
            outputs: [{ id: 'generated', label: 'Response', type: 'data' }],
          },
        },
        {
          id: 'create_task_1',
          type: 'action',
          position: { x: 1000, y: 100 },
          data: {
            label: 'Create Urgent Task',
            type: 'action',
            config: {
              actionType: 'create_task',
              task: {
                title: 'Urgent: {{trigger.subject}}',
                priority: 'high',
                assignee: 'support_team',
              },
            },
            icon: 'CheckSquare',
            color: '#3b82f6',
            inputs: [{ id: 'input', label: 'Email Data', type: 'data', required: true }],
            outputs: [{ id: 'created', label: 'Task Created', type: 'data' }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'trigger_1', target: 'ai_classify_1', sourceHandle: 'trigger', targetHandle: 'input' },
        { id: 'e2', source: 'ai_classify_1', target: 'condition_1', sourceHandle: 'generated', targetHandle: 'input' },
        { id: 'e3', source: 'condition_1', target: 'create_task_1', sourceHandle: 'true', targetHandle: 'input' },
        { id: 'e4', source: 'condition_1', target: 'ai_respond_1', sourceHandle: 'false', targetHandle: 'input' },
      ],
    },
  },
  {
    id: 'data-enrichment',
    name: 'Lead Data Enrichment',
    description: 'Automatically enrich lead data using AI and external APIs',
    category: 'data-management',
    icon: 'Database',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          position: { x: 100, y: 200 },
          data: {
            label: 'Weekly Schedule',
            type: 'trigger',
            config: {
              triggerType: 'schedule',
              schedule: { cron: '0 9 * * 1' }, // Every Monday at 9 AM
            },
            icon: 'Calendar',
            color: '#22c55e',
            outputs: [{ id: 'trigger', label: 'Triggered', type: 'trigger' }],
          },
        },
        {
          id: 'filter_1',
          type: 'filter',
          position: { x: 400, y: 200 },
          data: {
            label: 'Filter Incomplete Leads',
            type: 'filter',
            config: {
              expression: '!item.company_size || !item.industry',
            },
            icon: 'Filter',
            color: '#f59e0b',
            inputs: [{ id: 'input', label: 'Leads', type: 'data', required: true }],
            outputs: [{ id: 'filtered', label: 'Incomplete Leads', type: 'data' }],
          },
        },
        {
          id: 'ai_enrich_1',
          type: 'ai',
          position: { x: 700, y: 200 },
          data: {
            label: 'AI Data Enrichment',
            type: 'ai',
            config: {
              prompt: `Enrich this lead data by inferring missing information:
              Lead: {{input}}
              
              Based on the company name and any available information, provide:
              - Company size estimate
              - Industry classification
              - Potential use cases for our product
              - Recommended approach
              
              Be conservative and only add information you're confident about.`,
              model: 'gpt-4',
            },
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Lead', type: 'data', required: true }],
            outputs: [{ id: 'generated', label: 'Enriched Data', type: 'data' }],
          },
        },
        {
          id: 'update_lead_1',
          type: 'action',
          position: { x: 1000, y: 200 },
          data: {
            label: 'Update Lead Record',
            type: 'action',
            config: {
              actionType: 'update_lead',
              lead: { field: 'enriched_data', value: '{{ai_enrich_1_output}}', operation: 'set' },
            },
            icon: 'Database',
            color: '#3b82f6',
            inputs: [{ id: 'input', label: 'Enriched Data', type: 'data', required: true }],
            outputs: [{ id: 'updated', label: 'Updated', type: 'data' }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'trigger_1', target: 'filter_1', sourceHandle: 'trigger', targetHandle: 'input' },
        { id: 'e2', source: 'filter_1', target: 'ai_enrich_1', sourceHandle: 'filtered', targetHandle: 'input' },
        { id: 'e3', source: 'ai_enrich_1', target: 'update_lead_1', sourceHandle: 'generated', targetHandle: 'input' },
      ],
    },
  },
  
  // AI Agent Workflow Templates
  {
    id: 'simple-ai-assistant',
    name: 'Simple AI Assistant',
    description: 'Basic AI assistant workflow with input, LLM processing, and output',
    category: 'ai-agent',
    icon: 'Bot',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'input_1',
          type: 'input',
          position: { x: 100, y: 200 },
          data: {
            label: 'User Input',
            type: 'input',
            config: {},
            userQuery: 'How can I improve my sales process?',
            icon: 'Play',
            color: '#22c55e',
            outputs: [{ id: 'output', label: 'Query', type: 'data' }],
          },
        },
        {
          id: 'llm_1',
          type: 'llm',
          position: { x: 400, y: 200 },
          data: {
            label: 'AI Assistant',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a helpful business consultant specializing in sales optimization. Provide actionable advice.',
            temperature: 0.7,
            maxTokens: 1000,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Query', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Response', type: 'data' }],
          },
        },
        {
          id: 'output_1',
          type: 'output',
          position: { x: 700, y: 200 },
          data: {
            label: 'Assistant Response',
            type: 'output',
            config: {},
            output: '',
            icon: 'FileText',
            color: '#ef4444',
            inputs: [{ id: 'input', label: 'Response', type: 'data', required: true }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'input_1', target: 'llm_1', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e2', source: 'llm_1', target: 'output_1', sourceHandle: 'output', targetHandle: 'input' },
      ],
    },
  },
  {
    id: 'prompt-chaining-analysis',
    name: 'Prompt Chaining Analysis',
    description: 'Multi-step analysis workflow with sequential AI processing',
    category: 'ai-agent',
    icon: 'Brain',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'input_1',
          type: 'input',
          position: { x: 100, y: 200 },
          data: {
            label: 'Business Data',
            type: 'input',
            config: {},
            userQuery: 'Analyze our Q4 sales performance and provide recommendations',
            icon: 'Play',
            color: '#22c55e',
            outputs: [{ id: 'output', label: 'Data', type: 'data' }],
          },
        },
        {
          id: 'llm_1',
          type: 'llm',
          position: { x: 350, y: 150 },
          data: {
            label: 'Data Analyzer',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a data analyst. Analyze the provided business data and identify key patterns, trends, and insights.',
            temperature: 0.3,
            maxTokens: 800,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Raw Data', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Analysis', type: 'data' }],
          },
        },
        {
          id: 'llm_2',
          type: 'llm',
          position: { x: 350, y: 300 },
          data: {
            label: 'Insight Generator',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a business strategist. Based on the data analysis, generate actionable insights and recommendations.',
            temperature: 0.5,
            maxTokens: 800,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Analysis', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Insights', type: 'data' }],
          },
        },
        {
          id: 'llm_3',
          type: 'llm',
          position: { x: 600, y: 225 },
          data: {
            label: 'Report Synthesizer',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a business report writer. Combine the analysis and insights into a comprehensive executive summary with clear action items.',
            temperature: 0.4,
            maxTokens: 1200,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [
              { id: 'analysis', label: 'Analysis', type: 'data', required: true },
              { id: 'insights', label: 'Insights', type: 'data', required: true }
            ],
            outputs: [{ id: 'output', label: 'Final Report', type: 'data' }],
          },
        },
        {
          id: 'output_1',
          type: 'output',
          position: { x: 850, y: 225 },
          data: {
            label: 'Executive Summary',
            type: 'output',
            config: {},
            output: '',
            icon: 'FileText',
            color: '#ef4444',
            inputs: [{ id: 'input', label: 'Report', type: 'data', required: true }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'input_1', target: 'llm_1', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e2', source: 'input_1', target: 'llm_2', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e3', source: 'llm_1', target: 'llm_3', sourceHandle: 'output', targetHandle: 'analysis' },
        { id: 'e4', source: 'llm_2', target: 'llm_3', sourceHandle: 'output', targetHandle: 'insights' },
        { id: 'e5', source: 'llm_3', target: 'output_1', sourceHandle: 'output', targetHandle: 'input' },
      ],
    },
  },
  {
    id: 'ai-agent-with-tools',
    name: 'AI Agent with Tools',
    description: 'Advanced AI agent workflow with external tool integration',
    category: 'ai-agent',
    icon: 'Bot',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'input_1',
          type: 'input',
          position: { x: 100, y: 200 },
          data: {
            label: 'User Request',
            type: 'input',
            config: {},
            userQuery: 'Research our competitor pricing and create a comparison report',
            icon: 'Play',
            color: '#22c55e',
            outputs: [{ id: 'output', label: 'Request', type: 'data' }],
          },
        },
        {
          id: 'agent_1',
          type: 'agent',
          position: { x: 400, y: 200 },
          data: {
            label: 'Research Agent',
            type: 'agent',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a market research agent. Use available tools to gather information and create comprehensive reports.',
            temperature: 0.6,
            maxTokens: 1500,
            tools: ['web_search', 'sheets_manager'],
            icon: 'Bot',
            color: '#f59e0b',
            inputs: [{ id: 'input', label: 'Request', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Research', type: 'data' }],
          },
        },
        {
          id: 'tool_1',
          type: 'tool',
          position: { x: 400, y: 350 },
          data: {
            label: 'Web Search',
            type: 'tool',
            config: {},
            selectedAction: 'web_search',
            icon: 'Wrench',
            color: '#06b6d4',
            inputs: [{ id: 'input', label: 'Query', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Results', type: 'data' }],
          },
        },
        {
          id: 'tool_2',
          type: 'tool',
          position: { x: 400, y: 50 },
          data: {
            label: 'Google Sheets',
            type: 'tool',
            config: {},
            selectedAction: 'sheets_manager',
            icon: 'Wrench',
            color: '#06b6d4',
            inputs: [{ id: 'input', label: 'Data', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Sheet Created', type: 'data' }],
          },
        },
        {
          id: 'output_1',
          type: 'output',
          position: { x: 700, y: 200 },
          data: {
            label: 'Final Report',
            type: 'output',
            config: {},
            output: '',
            icon: 'FileText',
            color: '#ef4444',
            inputs: [{ id: 'input', label: 'Report', type: 'data', required: true }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'input_1', target: 'agent_1', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e2', source: 'agent_1', target: 'output_1', sourceHandle: 'output', targetHandle: 'input' },
      ],
    },
  },
  {
    id: 'parallel-processing-workflow',
    name: 'Parallel AI Processing',
    description: 'Parallel processing workflow for handling multiple AI tasks simultaneously',
    category: 'ai-agent',
    icon: 'Shuffle',
    is_public: true,
    created_at: new Date().toISOString(),
    canvas_state: {
      nodes: [
        {
          id: 'input_1',
          type: 'input',
          position: { x: 100, y: 250 },
          data: {
            label: 'Content Input',
            type: 'input',
            config: {},
            userQuery: 'Create marketing content for our new product launch',
            icon: 'Play',
            color: '#22c55e',
            outputs: [{ id: 'output', label: 'Content Brief', type: 'data' }],
          },
        },
        {
          id: 'llm_1',
          type: 'llm',
          position: { x: 350, y: 150 },
          data: {
            label: 'Email Writer',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are an email marketing specialist. Create compelling email campaigns.',
            temperature: 0.8,
            maxTokens: 800,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Brief', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Email Content', type: 'data' }],
          },
        },
        {
          id: 'llm_2',
          type: 'llm',
          position: { x: 350, y: 250 },
          data: {
            label: 'Social Media Creator',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a social media content creator. Create engaging posts for various platforms.',
            temperature: 0.9,
            maxTokens: 600,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Brief', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Social Posts', type: 'data' }],
          },
        },
        {
          id: 'llm_3',
          type: 'llm',
          position: { x: 350, y: 350 },
          data: {
            label: 'Blog Writer',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a technical blog writer. Create informative and engaging blog posts.',
            temperature: 0.7,
            maxTokens: 1200,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [{ id: 'input', label: 'Brief', type: 'data', required: true }],
            outputs: [{ id: 'output', label: 'Blog Post', type: 'data' }],
          },
        },
        {
          id: 'llm_4',
          type: 'llm',
          position: { x: 600, y: 250 },
          data: {
            label: 'Content Coordinator',
            type: 'llm',
            config: {},
            provider: 'openai',
            model: 'gpt-4',
            systemPrompt: 'You are a marketing coordinator. Compile and organize all content into a cohesive marketing plan.',
            temperature: 0.5,
            maxTokens: 1000,
            icon: 'Brain',
            color: '#8b5cf6',
            inputs: [
              { id: 'email', label: 'Email', type: 'data', required: true },
              { id: 'social', label: 'Social', type: 'data', required: true },
              { id: 'blog', label: 'Blog', type: 'data', required: true }
            ],
            outputs: [{ id: 'output', label: 'Marketing Plan', type: 'data' }],
          },
        },
        {
          id: 'output_1',
          type: 'output',
          position: { x: 850, y: 250 },
          data: {
            label: 'Marketing Package',
            type: 'output',
            config: {},
            output: '',
            icon: 'FileText',
            color: '#ef4444',
            inputs: [{ id: 'input', label: 'Plan', type: 'data', required: true }],
          },
        },
      ],
      edges: [
        { id: 'e1', source: 'input_1', target: 'llm_1', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e2', source: 'input_1', target: 'llm_2', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e3', source: 'input_1', target: 'llm_3', sourceHandle: 'output', targetHandle: 'input' },
        { id: 'e4', source: 'llm_1', target: 'llm_4', sourceHandle: 'output', targetHandle: 'email' },
        { id: 'e5', source: 'llm_2', target: 'llm_4', sourceHandle: 'output', targetHandle: 'social' },
        { id: 'e6', source: 'llm_3', target: 'llm_4', sourceHandle: 'output', targetHandle: 'blog' },
        { id: 'e7', source: 'llm_4', target: 'output_1', sourceHandle: 'output', targetHandle: 'input' },
      ],
    },
  },
];